"use client";

import { contactInfo, overseasWarehouses } from "@/lib/contacts";
import { useTranslations } from "next-intl";
import ContactFormTwo from "../InquryForm/ContactFormTwo";
const MAP_SRC =
	"https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3307.057198235068!2d-118.153064222872!3d34.01674271960168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2cfaefe34db15%3A0x21a9feb10448524f!2s5650%20Grace%20Pl%2C%20Commerce%2C%20CA%2090022!5e0!3m2!1szh-CN!2sus!4v1778641787782!5m2!1szh-CN!2sus";

function ArrowIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
			<path d="M3 8h9M8.5 3.5 13 8l-4.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
		</svg>
	);
}

function ContactIcon({ type }: { type: "phone" | "email" | "whatsapp" | "address" }) {
	const paths = {
		phone: (
			<path
				d="M8.5 5.5c.4 4.7 2.8 7.5 7.6 8l2-2.1a1.3 1.3 0 0 1 1.3-.32c1.43.46 2.94.72 4.5.72.72 0 1.3.58 1.3 1.3v3.2c0 .72-.58 1.3-1.3 1.3C12.72 17.6 4 8.88 4 .9 4 .18 4.58-.4 5.3-.4h3.2c.72 0 1.3.58 1.3 1.3 0 1.56.26 3.07.72 4.5.15.46.03.97-.32 1.3l-1.7 1.8Z"
				transform="translate(-2 3)"
				stroke="currentColor"
				strokeWidth="1.4"
				strokeLinejoin="round"
			/>
		),
		email: (
			<>
				<rect x="3" y="5" width="18" height="14" rx="1.8" stroke="currentColor" strokeWidth="1.4" />
				<path d="m4 6 8 6.2L20 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
			</>
		),
		whatsapp: (
			<>
				<path
					d="M19.2 12.1a7.2 7.2 0 0 1-10.7 6.3L5 19.3l1-3.4a7.2 7.2 0 1 1 13.2-3.8Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeLinejoin="round"
				/>
				<path
					d="M9.5 8.6c.2-.45.42-.48.72-.48h.52c.18 0 .42.06.64.52.24.46.8 1.58.86 1.7.07.12.12.27.02.45-.1.2-.16.3-.32.48l-.46.5c-.15.15-.3.32-.13.62.18.3.78 1.28 1.68 2.08 1.15 1.02 2.12 1.34 2.42 1.5.3.15.48.13.66-.08.18-.2.76-.88.96-1.18.2-.3.4-.25.68-.15.28.1 1.78.84 2.08.98.3.15.5.22.58.35.07.12.07.72-.18 1.42-.24.7-1.43 1.34-1.98 1.38-.5.04-1.12.06-1.8-.12-.42-.12-.96-.3-1.64-.6-2.88-1.24-4.76-4.12-4.9-4.32-.14-.2-1.17-1.56-1.17-2.97 0-1.4.74-2.08 1-2.36Z"
					transform="scale(.72) translate(4.2 1.5)"
					fill="currentColor"
				/>
			</>
		),
		address: (
			<>
				<path
					d="M12 21s6-5.1 6-11a6 6 0 0 0-12 0c0 5.9 6 11 6 11Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeLinejoin="round"
				/>
				<circle cx="12" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.4" />
			</>
		),
	};

	return (
		<svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
			{paths[type]}
		</svg>
	);
}

function TrustMark({ value, label }: { value: string; label: string }) {
	return (
		<div className="border-brand-ink/10 border-t pt-4 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0">
			<p className="font-display text-brand-ink text-[clamp(28px,3vw,44px)] leading-none">{value}</p>
			<p className="font-body text-brand-muted mt-2 text-[13px] leading-[1.5]">{label}</p>
		</div>
	);
}

export default function ConcatPage({ locale }: { locale: string }) {
	const t = useTranslations("contact");
	const telHref = `tel:${contactInfo.phone.replace(/\s/g, "")}`;
	const whatsappHref = `https://wa.me/${contactInfo.whatsapp.replace(/\D/g, "")}`;
	const contactMethods = [
		{
			type: "phone" as const,
			label: t("methodPhone"),
			value: contactInfo.phone,
			href: telHref,
			cta: t("methodPhoneCta"),
		},
		{
			type: "email" as const,
			label: t("methodEmail"),
			emails: [
				{ label: t("methodEmailPreSales"), value: contactInfo.email },
				{ label: t("methodEmailAfterSales"), value: contactInfo.email2 },
			],
			cta: t("methodEmailCta"),
		},
		{
			type: "whatsapp" as const,
			label: t("methodWhatsapp"),
			value: contactInfo.whatsapp,
			href: whatsappHref,
			cta: t("methodWhatsappCta"),
		},
	];
	const trustMarks = [
		{ value: t("statResponseValue"), label: t("statResponseLabel") },
		{ value: t("statCertificationValue"), label: t("statCertificationLabel") },
		{ value: t("statSupportValue"), label: t("statSupportLabel") },
	];
	const processSteps = [
		{ value: t("processStep1Value"), title: t("processStep1Title"), desc: t("processStep1Desc") },
		{ value: t("processStep2Value"), title: t("processStep2Title"), desc: t("processStep2Desc") },
		{ value: t("processStep3Value"), title: t("processStep3Title"), desc: t("processStep3Desc") },
	];

	return (
		<main className="bg-[#f7f4ee]">
			<section className="w-full bg-[#f5f5f5]">
				<div className="mx-auto grid w-full max-w-[1580px] grid-cols-1 gap-0 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-[1.08fr_0.92fr] lg:px-12 lg:py-20">
					<div className="bg-white px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-16">
						<span className="text-brand-fg font-body inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em]">
							<span aria-hidden className="bg-brand h-px w-10" />
							{t("heroEyebrow")}
						</span>
						<h1 className="font-display text-brand-ink mt-5 max-w-[12ch] text-[clamp(42px,5.7vw,88px)] leading-[0.98]">
							{t("heroTitle")}
						</h1>
						<p className="font-body text-brand-muted mt-6 max-w-[62ch] text-[15px] leading-[1.78] sm:text-[16px]">
							{t("heroDesc")}
						</p>

						<div className="mt-8 flex flex-col gap-3 sm:flex-row">
							<a
								href="#contact-form"
								className="bg-brand font-body hover:bg-brand-accent focus-visible:outline-brand group inline-flex min-h-[46px] items-center justify-center gap-2 px-6 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
							>
								{t("heroPrimaryCta")}
								<ArrowIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
							</a>
							<a
								href={whatsappHref}
								target="_blank"
								rel="noreferrer"
								className="hover:text-brand-fg border-brand-ink/25 font-body text-brand-ink hover:border-brand focus-visible:outline-brand inline-flex min-h-[46px] items-center justify-center border px-6 text-[13px] font-bold uppercase tracking-[0.16em] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
							>
								{t("heroSecondaryCta")}
							</a>
						</div>
					</div>

					<aside className="bg-brand-ink flex flex-col justify-between p-6 text-white sm:p-10 lg:p-12">
						<div>
							<p className="font-body text-[12px] uppercase tracking-[0.22em] text-white/45">
								{t("responseEyebrow")}
							</p>
							<h2 className="font-display mt-4 text-[clamp(26px,3vw,42px)] leading-[1.12] text-white">
								{t("responseTitle")}
							</h2>
							<p className="text-white/68 font-body mt-5 text-[14px] leading-[1.75] sm:text-[15px]">
								{t("responseDesc")}
							</p>
						</div>

						<div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3 lg:grid-cols-1">
							{trustMarks.map((item) => (
								<div key={item.label} className="border-white/12 border-t pt-5">
									<p className="font-display text-brand text-[34px] leading-none">{item.value}</p>
									<p className="font-body mt-2 text-[13px] leading-[1.55] text-white/65">{item.label}</p>
								</div>
							))}
						</div>
					</aside>
				</div>
			</section>

			<section className="w-full bg-white py-14 sm:py-16 md:py-20">
				<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
					<header className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
						<div>
							<span className="text-brand-fg font-body inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em]">
								<span aria-hidden className="bg-brand h-px w-10" />
								{t("methodsEyebrow")}
							</span>
							<h2 className="font-display text-brand-ink mt-4 max-w-[14ch] text-[clamp(32px,3.6vw,60px)] leading-[1.08]">
								{t("methodsTitle")}
							</h2>
						</div>
						<p className="font-body text-brand-muted max-w-[52ch] text-[15px] leading-[1.72]">
							{t("methodsDesc")}
						</p>
					</header>

					<div className="border-brand-ink/10 mt-10 grid grid-cols-1 border md:grid-cols-3">
						{contactMethods.map((method) => {
							const cardClassName =
								"group flex min-h-[190px] flex-col justify-between border-b border-brand-ink/10 bg-white p-6 text-brand-ink transition-colors duration-200 last:border-b-0 hover:bg-brand-tint hover:text-brand-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand md:border-b-0 md:border-r md:last:border-r-0 lg:p-8";

							if (method.type === "email") {
								return (
									<article key={method.type} className={cardClassName}>
										<div className="flex items-start justify-between gap-4">
											<span className="text-brand-fg">
												<ContactIcon type={method.type} />
											</span>
										</div>
										<div className="mt-8">
											<p className="font-body text-brand-muted text-[12px] font-semibold uppercase tracking-[0.18em]">
												{method.label}
											</p>
											<div className="mt-3 flex flex-col gap-3">
												{method.emails.map((email) => (
													<a
														key={email.value}
														href={`mailto:${email.value}`}
														className="group/email border-brand-ink/10 focus-visible:outline-brand block break-words border-t pt-3 first:border-t-0 first:pt-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
													>
														<span className="font-body text-brand-muted block text-[11px] font-semibold uppercase tracking-[0.16em]">
															{email.label}
														</span>
														<span className="font-display text-brand-ink group-hover/email:text-brand-fg mt-1 block text-[20px] leading-[1.2] transition-colors duration-200">
															{email.value}
														</span>
													</a>
												))}
											</div>
											<p className="text-brand-fg font-body mt-4 text-[13px] font-semibold uppercase tracking-[0.14em]">
												{method.cta}
											</p>
										</div>
									</article>
								);
							}

							return (
								<a
									key={method.type}
									href={method.href}
									target={method.type === "whatsapp" ? "_blank" : undefined}
									rel={method.type === "whatsapp" ? "noreferrer" : undefined}
									className={cardClassName}
								>
									<div className="flex items-start justify-between gap-4">
										<span className="text-brand-fg">
											<ContactIcon type={method.type} />
										</span>
										<ArrowIcon className="text-brand-ink/35 group-hover:text-brand h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
									</div>
									<div className="mt-8">
										<p className="font-body text-brand-muted text-[12px] font-semibold uppercase tracking-[0.18em]">
											{method.label}
										</p>
										<p className="font-display text-brand-ink mt-2 break-words text-[22px] leading-[1.2]">
											{method.value}
										</p>
										<p className="text-brand-fg font-body mt-4 text-[13px] font-semibold uppercase tracking-[0.14em]">
											{method.cta}
										</p>
									</div>
								</a>
							);
						})}
					</div>
				</div>
			</section>

			<section className="bg-brand-tint w-full py-14 sm:py-16 md:py-20">
				<div className="mx-auto grid w-full max-w-[1580px] grid-cols-1 gap-6 px-5 sm:px-8 lg:grid-cols-[0.86fr_1.14fr] lg:px-12">
					<div className="bg-white p-6 sm:p-8 lg:p-10">
						<span className="text-brand-fg font-body inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em]">
							<span aria-hidden className="bg-brand h-px w-10" />
							{t("factoryEyebrow")}
						</span>
						<h2 className="font-display text-brand-ink mt-4 text-[clamp(30px,3vw,52px)] leading-[1.1]">
							{t("factoryTitle")}
						</h2>
						<p className="font-body text-brand-muted mt-5 text-[15px] leading-[1.75]">{t("factoryDesc")}</p>

						<div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
							{trustMarks.map((item) => (
								<TrustMark key={item.label} value={item.value} label={item.label} />
							))}
						</div>

						<div className="border-brand-ink/10 mt-10 flex items-start gap-4 border-t pt-6">
							<span className="text-brand-fg shrink-0">
								<ContactIcon type="address" />
							</span>
							<div>
								<p className="font-body text-brand-muted text-[12px] font-semibold uppercase tracking-[0.18em]">
									{t("addressLabel")}
								</p>
								<p className="font-body text-brand-ink mt-2 text-[15px] leading-[1.65]">
									{overseasWarehouses[0].address}
								</p>
							</div>
						</div>
					</div>

					<div className="min-h-[360px] overflow-hidden bg-white lg:min-h-[560px]">
						<iframe
							src={MAP_SRC}
							width="100%"
							height="100%"
							title={t("mapTitle")}
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
							className="h-[360px] w-full border-0 lg:h-full"
						/>
					</div>
				</div>
			</section>

			<section className="w-full bg-white py-14 sm:py-16 md:py-20" aria-labelledby="warehouses-heading">
				<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
					<header className="max-w-[62ch]">
						<span className="text-brand-fg font-body inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em]">
							<span aria-hidden className="bg-brand h-px w-10" />
							{t("warehousesEyebrow")}
						</span>
						<h2
							id="warehouses-heading"
							className="font-display text-brand-ink mt-4 text-[clamp(30px,3vw,52px)] leading-[1.1]"
						>
							{t("warehousesTitle")}
						</h2>
						<p className="font-body text-brand-muted mt-5 text-[15px] leading-[1.75]">
							{t("warehousesDesc")}
						</p>
					</header>

					<ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{overseasWarehouses.map((row) => (
							<li key={row.translationKey} className="border-brand-ink/10 bg-brand-tint border p-5 sm:p-6">
								<p className="font-body text-brand-muted text-[12px] font-semibold uppercase tracking-[0.18em]">
									{t(row.translationKey)}
								</p>
								<p className="font-body text-brand-ink mt-3 text-[14px] leading-[1.65]">{row.address}</p>
							</li>
						))}
					</ul>
				</div>
			</section>

			<section id="contact-form" className="w-full bg-[#f5f5f5] py-14 sm:py-16 md:py-20">
				<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
					<div className="mb-10 grid grid-cols-1 gap-8 md:grid-cols-[0.9fr_1.1fr] md:items-end">
						<div>
							<span className="text-brand-fg font-body inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em]">
								<span aria-hidden className="bg-brand h-px w-10" />
								{t("formSectionEyebrow")}
							</span>
							<h2 className="font-display text-brand-ink mt-4 text-[clamp(32px,3.6vw,60px)] leading-[1.08]">
								{t("formSectionTitle")}
							</h2>
						</div>
						<p className="font-body text-brand-muted max-w-[62ch] text-[15px] leading-[1.72] md:justify-self-end">
							{t("formSectionDesc")}
						</p>
					</div>
				</div>

				<ContactFormTwo locale={locale} className="!px-5 sm:!px-8 lg:!px-12" />
			</section>

			<section className="w-full bg-white py-14 sm:py-16 md:py-20">
				<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
					<div className="border-brand-ink/10 grid grid-cols-1 gap-10 border-t pt-10 lg:grid-cols-[0.7fr_1.3fr]">
						<div>
							<span className="text-brand-fg font-body inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.22em]">
								<span aria-hidden className="bg-brand h-px w-10" />
								{t("processEyebrow")}
							</span>
							<h2 className="font-display text-brand-ink mt-4 text-[clamp(30px,3vw,52px)] leading-[1.1]">
								{t("processTitle")}
							</h2>
						</div>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							{processSteps.map((step) => (
								<article key={step.value} className="bg-brand-tint p-6">
									<p className="font-display text-brand text-[34px] leading-none">{step.value}</p>
									<h3 className="font-display text-brand-ink mt-6 text-[22px] leading-[1.2]">{step.title}</h3>
									<p className="font-body text-brand-muted mt-3 text-[14px] leading-[1.65]">{step.desc}</p>
								</article>
							))}
						</div>
					</div>
				</div>
			</section>
		</main>
	);
}

export function HomeTaile({ msg }: { msg: string }) {
	return (
		<div className="mb-[40px] bg-[url(/image/gb.png)] bg-[length:100%_100%] bg-no-repeat py-[80px]">
			<div className="container max-md:mt-5">
				<h2 className="text-bloak text-center text-[42px] font-medium text-white max-md:text-2xl">{msg}</h2>
			</div>
		</div>
	);
}
